import { useEffect, type ReactNode } from "react";
import { BrandMark } from "../components/Icon";
import { EnvironmentIndicator } from "../layout/EnvironmentIndicator";
import { AuthHeroIllustration } from "./AuthHeroIllustration";

export type AuthVariant = "signin" | "signup" | "neutral";

// Panel copy must match claims made on the marketing homepage
// (site/index.html) — no invented numbers or customers.
const ASIDE_CONTENT: Record<AuthVariant, { tagline: string; bullets: string[] }> = {
  signin: {
    tagline: "Welcome back.",
    bullets: [
      "One dashboard for your campaigns or your sites.",
      "Every sponsored result is reviewed and clearly labelled.",
    ],
  },
  signup: {
    tagline: "One ad network, two sides.",
    bullets: [
      "Advertisers create short text ads that appear inside AI tool results.",
      "Publishers show one clearly labelled ad in their MCP tools and earn a revenue share.",
      "Every ad is reviewed by staff before it runs.",
    ],
  },
  // Recovery and invitation screens: neutral and minimal, no pitch.
  neutral: {
    tagline: "The ad network for MCP tools.",
    bullets: [],
  },
};

export function AuthLayout({
  title,
  children,
  footer,
  variant = "neutral",
}: {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  variant?: AuthVariant;
}) {
  useEffect(() => {
    document.title = `${title} · VADS`;
  }, [title]);
  const { tagline, bullets } = ASIDE_CONTENT[variant];
  return (
    <main className="auth-page" id="main">
      <div className="auth-shell">
        {/* Brand/marketing panel: left column on wide screens, a slim header
            above the form on narrow ones (.auth-aside in shell.css). It's
            first in both visual and DOM order — CSS `order` would only
            fight the browser, which reorders focus to match visual order
            anyway — but it holds a single link and no other focusable
            content, so it adds one tab stop before the form, not a detour
            through decorative art or a bullet list. */}
        <aside className="auth-aside" aria-label="About VADS">
          <a className="auth-aside-back" href="https://vads.au">
            ← vads.au
          </a>
          <div className="auth-aside-brand">
            <BrandMark />
            <span>VADS</span>
          </div>
          <p className="auth-aside-tagline">{tagline}</p>
          {bullets.length > 0 ? (
            <>
              <div className="auth-aside-art" aria-hidden="true">
                <AuthHeroIllustration />
              </div>
              <ul className="auth-aside-bullets">
                {bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </>
          ) : null}
        </aside>
        <div className="auth-card-wrap">
          <div className="auth-card stack">
            <div className="card stack">
              <div className="row-between">
                <h1 style={{ fontSize: "var(--text-h2)" }}>{title}</h1>
                <EnvironmentIndicator />
              </div>
              {children}
            </div>
            {footer ? (
              <div className="small muted" style={{ textAlign: "center" }}>
                {footer}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </main>
  );
}
